import { AFFINITY, AWAKEN, FORM_BIT, FORM_STAGE, UNIQUE_FORMS, formClash, type FormKey, type UniqueId, type UniqueSlot, DRAGON, BARBARIAN, GEAR_SETS, GEM_CAP_BP, type SetId, GEM_CUT_BP, WORK_MULTIPLIER, type GearAttr, GEAR_BASES, SAME_RING_BP, WEIGHT_SET_BP, WORN_SLOTS, type GearTrait, AILMENTS, AILMENT_MARKS, CATCH_UP, GEAR_CAPS, mareaXpBp, OPPOSITE, RES_TYPE, attrValue, isResAttr, GEAR_EFFECT_FX, GEAR_STACK_BP, GEM_ATTR, HEROES, MAX_LEVEL,  UNIQUES, GLYPH, GLYPH_IDS, GLYPHS, SKILL_NODES, SKILL_TREES, TOMES, glyphFits, glyphSlotsOpen, type GlyphId, type GlyphSource, UNIQUE_EFFECT_BP, XP_TO_NEXT, type CarryMode, type DamageType, type ForkId, type GearEffect, type PaladinAura, type SkillId } from '@somnhold/content';
import { imax, imin, mulBp } from '../math/fixed.ts';
import type { GearMods, Variants } from '../battle/create.ts';
import type { BattleState } from '../types.ts';
import { event } from './core.ts';
import { claimUniques, itemAttrs, itemBudget, itemSockets, lookOf, preparedGear, refreshUniques, weightSetOf } from './items.ts';
import type { CampaignState, HeroState, Loadout, LoadoutPick, Ms } from './types.ts';

/**
 * Whether a hero lags the rest of the group (CATCH_UP): more than `belowBy` levels under the average
 * level of the others still in the campaign. Alone, never.
 */
export const behindGroup = (s: CampaignState, pid: string): boolean => {
  const p = s.players.find((x) => x.id === pid);
  if (!p) return false;
  let sum = 0;
  let n = 0;
  for (const o of s.players) {
    if (o.id === pid || o.left) continue;
    sum += o.hero.level;
    n++;
  }
  if (n === 0) return false;
  // level < average − belowBy, kept in integers: level × n < sum − belowBy × n.
  return p.hero.level * n < sum - CATCH_UP.belowBy * n;
};

/** Adds XP and levels up (SPEC §14): 400 × n^1.5 to go from n to n+1, one skill point per level. */
/** Gives a hero experience; returns what it actually got (catch-up included, 0 at the level cap). */
export const grantXp = (s: CampaignState, pid: string, amount: number, now: Ms): number => {
  const p = s.players.find((x) => x.id === pid);
  if (!p || amount <= 0) return 0;
  const h = p.hero;
  if (h.level >= MAX_LEVEL) return 0;
  // Behind the group: more XP until the hero catches up.
  if (behindGroup(s, pid)) amount = mulBp(amount, CATCH_UP.xpBp);
  // The late Mareas pay more: the levels past the first season are steep.
  amount = mulBp(amount, mareaXpBp(s.mareaIndex));
  h.xp += amount;
  while (h.level < MAX_LEVEL) {
    const need = XP_TO_NEXT[h.level] as number;
    if (h.xp < need) break;
    h.xp -= need;
    h.level++;
    h.skillPoints++;
    h.freeRespec = true;
    event(s, now, 'levelUp', { pid, data: { level: h.level } });
  }
  // Its unique pieces' attributes follow it (docs/UNICAS.md §2.4); one kept for this level comes now.
  refreshUniques(h);
  claimUniques(s, p, now);
  if (h.level >= MAX_LEVEL) h.xp = 0;
  return amount;
};

/** Adds `share` (bp) of one rare effect to the gear modifiers. */
const addEffect = (m: GearMods, effect: GearEffect, share: number): void => {
  const fx = GEAR_EFFECT_FX;
  const add = (cur: number | undefined, v: number): number => (cur ?? 0) + mulBp(v, share);
  if (effect === 'burn') m.burnBp = add(m.burnBp, fx.burn.bp);
  else if (effect === 'lifesteal') m.lifestealBp = add(m.lifestealBp, fx.lifesteal.bp);
  else if (effect === 'thorns') m.thornsBp = add(m.thornsBp, fx.thorns.attackBp);
  else if (effect === 'swift') m.swiftBp = add(m.swiftBp, fx.swift.bp);
  else if (effect === 'fury') m.furyGearBp = add(m.furyGearBp, fx.fury.bp);
  else if (effect === 'execute') m.executeBp = add(m.executeBp, fx.execute.bp);
  else if (effect === 'chain') m.chainBp = add(m.chainBp, fx.chain.bp);
  else if (effect === 'frost') m.frostBp = add(m.frostBp, fx.frost.bp);
  else if (effect === 'cleave') m.cleaveBp = add(m.cleaveBp, fx.cleave.bp);
  else if (effect === 'guard') m.guardBp = add(m.guardBp, fx.guard.bp);
  else if (effect === 'echo') m.echoTicks = add(m.echoTicks, fx.echo.ticks);
  else m.bannerBp = add(m.bannerBp, fx.banner.bp);
};

/** Adds a base's trait at `f` (bp) of its strength to the gear modifiers (docs/EQUIPO.md §2). */
const addTrait = (m: GearMods, t: GearTrait, f: number, res: DamageType | undefined): void => {
  const v = (x: number | undefined): number => (x ? mulBp(x, f) : 0);
  m.damageBp += v(t.damageBp);
  m.hpBp += v(t.hpBp);
  m.attackSpeedBp += v(t.attackSpeedBp);
  m.cooldownBp += v(t.cooldownBp);
  m.armorBp += v(t.armorBp);
  if (t.critBp) m.critBp = (m.critBp ?? 0) + v(t.critBp);
  if (t.attackBp) m.attackBp = (m.attackBp ?? 0) + v(t.attackBp);
  if (t.rangeBp) m.rangeBp = (m.rangeBp ?? 0) + v(t.rangeBp);
  if (t.speedBp) m.swiftBp = (m.swiftBp ?? 0) + v(t.speedBp);
  if (t.bigFoeBp) m.bigFoeBp = (m.bigFoeBp ?? 0) + v(t.bigFoeBp);
  if (t.blockBp) m.blockBp = (m.blockBp ?? 0) + v(t.blockBp);
  if (t.skillBp) m.skillBp = (m.skillBp ?? 0) + v(t.skillBp);
  if (t.healBp) m.healGearBp = (m.healGearBp ?? 0) + v(t.healBp);
  if (t.summonBp) m.summonBp = (m.summonBp ?? 0) + v(t.summonBp);
  if (t.cleaveBp) m.cleaveTraitBp = imax(m.cleaveTraitBp ?? 0, v(t.cleaveBp));
  if (t.melee) m.melee = t.melee;
  if (t.stunEvery) {
    m.stunEvery = t.stunEvery;
    m.stunTicks = t.stunTicks ?? 0;
  }
  if (t.pierce) m.pierce = true;
  if (t.resBp && res) {
    const r = (m.resBp ??= {});
    r[res] = (r[res] ?? 0) + v(t.resBp);
  }
};

/** Gear attributes (forge pluses and gems included) as additive bp modifiers, before the caps (the hero sheet shows how far past them). */
export const gearModsUncapped = (h: HeroState): GearMods => {
  const m: GearMods = { damageBp: 0, hpBp: 0, attackSpeedBp: 0, cooldownBp: 0, armorBp: 0 };
  // A condition that lasts days rides along into every fight until it wears off or is cured.
  const ail = h.ailment ? AILMENTS[h.ailment.kind] : null;
  if (ail?.hpBp) m.hpBp += ail.hpBp;
  if (ail?.fearTicks) m.dreadTicks = ail.fearTicks;
  if (ail?.attackSpeedBp) m.attackSpeedBp += ail.attackSpeedBp;
  if (ail?.resBp) {
    const r = (m.resBp ??= {});
    for (const k of Object.keys(ail.resBp).sort() as DamageType[]) r[k] = (r[k] ?? 0) + (ail.resBp[k] ?? 0);
  }
  const seen: Partial<Record<GearEffect, number>> = {};
  const gemSum: Partial<Record<GearAttr, number>> = {};
  // Head, chest and feet of one weight: their traits count more (docs/EQUIPO.md §2).
  const weightSet = weightSetOf(h) !== null;
  for (const w of WORN_SLOTS) {
    const item = h.gear[w];
    if (!item) continue;
    // What its slot is worth: the rolls already carry it, its gem and its effect take it here (a piece from before bases: a whole slot).
    const budget = itemBudget(item);
    const attrs = itemAttrs(item);
    const keys = Object.keys(attrs).sort() as (keyof typeof attrs)[];
    keys.forEach((k) => {
      const v = attrs[k] ?? 0;
      if (k === 'damage') m.damageBp += v;
      else if (k === 'hp') m.hpBp += v;
      else if (k === 'attackSpeed') m.attackSpeedBp += v;
      else if (k === 'cooldown') m.cooldownBp += v;
      else if (k === 'crit') m.critBp = (m.critBp ?? 0) + attrValue('crit', v);
      else if (isResAttr(k)) {
        // Affinity: its type guarded, the opposite one left half as open.
        const t = RES_TYPE[k];
        const w = attrValue(k, v);
        const r = (m.resBp ??= {});
        r[t] = (r[t] ?? 0) + w;
        r[OPPOSITE[t]] = (r[OPPOSITE[t]] ?? 0) - mulBp(w, AFFINITY.oppositeBp);
      }
      else m.armorBp += v;
    });
    // Its settings' gems: each its cut's value times the slot's budget, summed by attribute (capped below).
    for (const g of itemSockets(item)) if (g) gemSum[GEM_ATTR[g.gem]] = (gemSum[GEM_ATTR[g.gem]] ?? 0) + mulBp(GEM_CUT_BP[g.cut] as number, budget);
    // A rare piece's effect: the same effect on a second and third piece counts for less (GEAR_STACK_BP); a unique's own is stronger.
    if (item.effect) {
      const n = seen[item.effect] ?? 0;
      seen[item.effect] = n + 1;
      addEffect(m, item.effect, mulBp(GEAR_STACK_BP[n] ?? 0, item.unique ? UNIQUE_EFFECT_BP + (item.awaken ?? 0) * AWAKEN.effectStepBp : budget));
    }
    // Its base's trait (not on unique pieces nor those from before bases): more with a weight set, a second ring of one form for less.
    if (item.base && !item.unique) {
      let f = 10000;
      if (weightSet && (w === 'head' || w === 'armor' || w === 'boots')) f += WEIGHT_SET_BP;
      if (w === 'ring2' && h.gear.ring?.base === item.base) f = SAME_RING_BP;
      addTrait(m, GEAR_BASES[item.base].trait, f, item.res);
    }
    // A unique piece of the hero's own class brings its effect.
    if (item.unique && UNIQUES[item.unique].cls === h.heroClass) {
      (m.uniques ??= []).push(item.unique);
      (m.uniqueLv ??= []).push(item.awaken ?? 0);
    }
  }
  // Sets (docs/EQUIPO.md §7): two pieces worn give the small bonus, all of them its effect at full strength too.
  const sets: Partial<Record<SetId, number>> = {};
  for (const w of WORN_SLOTS) {
    const id = h.gear[w]?.set;
    if (id) sets[id] = (sets[id] ?? 0) + 1;
  }
  for (const id of Object.keys(sets).sort() as SetId[]) {
    const n = sets[id] ?? 0;
    const def = GEAR_SETS[id];
    if (n >= 2) addTrait(m, def.two, 10000, undefined);
    if (n >= def.slots.length) {
      addTrait(m, def.three, 10000, undefined);
      addEffect(m, def.effect, 10000);
    }
  }
  // The gems of one attribute add up to GEM_CAP_BP (docs/EQUIPO.md §5): settings spread rather than stack.
  for (const k of Object.keys(gemSum).sort() as GearAttr[]) {
    const bp = imin(gemSum[k] ?? 0, GEM_CAP_BP);
    if (k === 'damage') m.damageBp += bp;
    else if (k === 'hp') m.hpBp += bp;
    else if (k === 'attackSpeed') m.attackSpeedBp += bp;
    else if (k === 'cooldown') m.cooldownBp += bp;
    else if (k === 'crit') m.critBp = (m.critBp ?? 0) + attrValue('crit', bp);
    else m.armorBp += bp;
  }
  return m;
};

/** Gear attributes as additive bp modifiers, held at GEAR_CAPS. */
export const gearMods = (h: HeroState): GearMods => {
  const m = gearModsUncapped(h);
  if (m.cooldownBp > GEAR_CAPS.cooldownBp) m.cooldownBp = GEAR_CAPS.cooldownBp;
  if (m.armorBp > GEAR_CAPS.armorBp) m.armorBp = GEAR_CAPS.armorBp;
  return m;
};

/** Bastion perk total from the skill tree (bp). */
export const perkBp = (h: HeroState, perk: 'repairDiscount' | 'workBonus' | 'ghostPower' | 'incursionLoot'): number => {
  let bp = 0;
  for (const n of SKILL_TREES[h.heroClass]) {
    const r = h.skills[n.id] ?? 0;
    if (r > 0 && n.effect.kind === 'bastion' && n.effect.perk === perk) bp += r * n.effect.bpPerRank;
  }
  return bp;
};

/**
 * Work a hero's help puts into a building site in `minutes` (ms): WORK_MULTIPLIER minutes a minute, the tree's
 * work bonus on top, and a barbarian's brute strength twice that. The bastion's clock shows the same pace.
 */
export const workProgressMs = (h: HeroState, minutes: number): number => {
  const brute = h.heroClass === 'barbarian' ? BARBARIAN.helpBp : 10000;
  return mulBp(mulBp(minutes * WORK_MULTIPLIER * 60000, 10000 + perkBp(h, 'workBonus')), brute);
};

/** Points spent in the skill tree. */
export const pointsSpent = (h: HeroState): number => Object.values(h.skills).reduce((a: number, b) => a + (b ?? 0), 0);

/**
 * The tree changed under the heroes (2026-10-23 and 2026-10-27, docs/PODERES.md): nodes that no longer
 * exist are dropped, so their points come back, and starting over is free once. Variants and bastion
 * perks kept their ids and stay learned. Idempotent; runs when a campaign loads.
 */
export const ensureSkillTree = (s: CampaignState): boolean => {
  let changed = false;
  for (const p of s.players) {
    const h = p.hero;
    const stale = Object.keys(h.skills).filter((id) => SKILL_NODES[id]?.cls !== h.heroClass);
    if (!stale.length) continue;
    for (const id of stale) delete h.skills[id];
    h.freeRespec = true;
    changed = true;
  }
  return changed;
};

/** The skills a hero knows in a slot: its class's own from the start, then the ones learned in that branch. */
export const knownSkills = (h: HeroState, slot: 0 | 1 | 2): SkillId[] => {
  const out: SkillId[] = [HEROES[h.heroClass].skills[slot]];
  for (const n of SKILL_TREES[h.heroClass]) {
    if (n.branch !== slot || n.effect.kind !== 'learn') continue;
    if (n.effect.tome ? tomeFull(h, slot) : (h.skills[n.id] ?? 0) > 0) out.push(n.effect.skill);
  }
  return out;
};

/** The skill each slot's tome teaches for this hero's class (none: that slot has no tome yet). */
export const tomeSkill = (h: Pick<HeroState, 'heroClass'>, slot: 0 | 1 | 2): SkillId | null => {
  const n = SKILL_TREES[h.heroClass].find((x) => x.branch === slot && x.effect.kind === 'learn' && x.effect.tome);
  return n && n.effect.kind === 'learn' ? n.effect.skill : null;
};

export const tomeFull = (h: HeroState, slot: 0 | 1 | 2): boolean => (h.tomePages?.[slot] ?? 0) >= TOMES.pages;

/**
 * Pages for a slot's tome (docs/PODERES.md §6); `slot` null: the tome furthest behind (an expedition's).
 * The log says so, and when the tome fills. Returns the slot that got them, if any.
 */
export const addPages = (s: CampaignState, pid: string, slot: 0 | 1 | 2 | null, n: number, now: Ms): 0 | 1 | 2 | null => {
  const p = s.players.find((x) => x.id === pid);
  if (!p || n <= 0) return null;
  const h = p.hero;
  const open = ([0, 1, 2] as const).filter((k) => tomeSkill(h, k) && !tomeFull(h, k));
  let k: 0 | 1 | 2 | null = slot;
  if (k === null) for (const o of open) if (k === null || (h.tomePages?.[o] ?? 0) < (h.tomePages?.[k] ?? 0)) k = o;
  if (k === null || !open.includes(k)) return null;
  const pages = (h.tomePages ??= [0, 0, 0]);
  pages[k] = imin(TOMES.pages, pages[k] + n);
  event(s, now, 'tomePage', { pid, data: { slot: k, skill: tomeSkill(h, k) ?? '', pages: pages[k], full: pages[k] >= TOMES.pages } });
  return k;
};

/** Whether the hero has this glyph complete (docs/GLIFOS.md §3). */
export const glyphOwned = (h: HeroState, g: GlyphId): boolean => (h.glyphFrags?.[g] ?? 0) >= GLYPH.fragments;

/**
 * A glyph fragment from a fight (docs/GLIFOS.md §3): of the glyph of that place the hero is closest to completing
 * (the first in order on a tie). With all of them complete, it melts into dust for the common storage. The log says so.
 * Returns the glyph that got it, if any.
 */
export const addGlyphFragment = (s: CampaignState, pid: string, source: GlyphSource, n: number, now: Ms): GlyphId | null => {
  const p = s.players.find((x) => x.id === pid);
  if (!p || n <= 0) return null;
  const h = p.hero;
  // The one closest to complete among those that pass `ok` (the first in order on a tie).
  const pick = (ok: (id: GlyphId) => boolean): GlyphId | null => {
    let best: GlyphId | null = null;
    for (const id of GLYPH_IDS) {
      if (!ok(id) || glyphOwned(h, id)) continue;
      if (best === null || (h.glyphFrags?.[id] ?? 0) > (h.glyphFrags?.[best] ?? 0)) best = id;
    }
    return best;
  };
  const g =
    pick((id) => GLYPHS[id].source === source) ??
    // Then the glyphs that come from here more slowly (bond, from the bosses once the conversions are complete).
    pick((id) => GLYPHS[id].also === source) ??
    // An expedition, needing friends at once, gives of any glyph once its own is complete.
    (source === 'expedition' ? pick(() => true) : null);
  if (g === null) {
    if (GLYPH_IDS.some((id) => GLYPHS[id].source === source)) s.storage.dust += n * GLYPH.dustPerFragment;
    return null;
  }
  const frags = (h.glyphFrags ??= {});
  const have = imin(GLYPH.fragments, (frags[g] ?? 0) + n);
  frags[g] = have;
  event(s, now, 'glyphFrag', { pid, data: { glyph: g, frags: have, full: have >= GLYPH.fragments } });
  return g;
};

/**
 * A whole glyph from a saga chapter (the Turtle 4 and 6, docs/UNICAS.md §1), once per hero and chapter: `want` if the
 * hero lacks it; else another conversion glyph it lacks (the one closest to complete, the first in order on a tie);
 * with all of them, three fragments of any (`addGlyphFragment`'s way, dust once every glyph is complete).
 * Returns the glyph it went to (null: already taken from this chapter, or melted into dust).
 */
export const grantSagaGlyph = (s: CampaignState, pid: string, chapter: number, want: GlyphId, now: Ms): GlyphId | null => {
  const p = s.players.find((x) => x.id === pid);
  if (!p) return null;
  const h = p.hero;
  if ((h.sagaGlyphs ?? []).includes(chapter)) return null;
  h.sagaGlyphs = [...(h.sagaGlyphs ?? []), chapter].sort((a, b) => a - b);
  const closest = (ok: (id: GlyphId) => boolean): GlyphId | null => {
    let best: GlyphId | null = null;
    for (const id of GLYPH_IDS) if (ok(id) && !glyphOwned(h, id) && (best === null || (h.glyphFrags?.[id] ?? 0) > (h.glyphFrags?.[best] ?? 0))) best = id;
    return best;
  };
  const g = !glyphOwned(h, want) ? want : closest((id) => !!GLYPHS[id].turns);
  if (g) {
    (h.glyphFrags ??= {})[g] = GLYPH.fragments;
    event(s, now, 'glyphFrag', { pid, data: { glyph: g, frags: GLYPH.fragments, full: true, saga: chapter } });
    return g;
  }
  let last: GlyphId | null = null;
  for (let i = 0; i < GLYPH.fragments; i++) {
    const any = closest(() => true);
    if (!any) {
      s.storage.dust += GLYPH.dustPerFragment;
      continue;
    }
    const frags = (h.glyphFrags ??= {});
    frags[any] = imin(GLYPH.fragments, (frags[any] ?? 0) + 1);
    event(s, now, 'glyphFrag', { pid, data: { glyph: any, frags: frags[any] as number, full: (frags[any] as number) >= GLYPH.fragments, saga: chapter } });
    last = any;
  }
  return last;
};

/**
 * Tomes came on 2026-10-27, after some groups had beaten a Marea boss or finished saga chapters: each hero's
 * ultimate tome gets the pages those would have given (the boss waves they fought, from the wave records'
 * XP, and the chapters they played). Idempotent, never takes pages away; runs when a campaign loads.
 */
export const ensureTomePages = (s: CampaignState): boolean => {
  let changed = false;
  for (const p of s.players) {
    let owed = 0;
    for (const w of s.waves) if (w.xp?.[p.id] && w.highlights.some((x) => x.startsWith('bossSlain:'))) owed += TOMES.boss;
    for (const played of s.saga?.done ?? []) if (played.includes(p.id)) owed += TOMES.saga;
    const n = imin(TOMES.pages, owed) - (p.hero.tomePages?.[2] ?? 0);
    if (n > 0 && addPages(s, p.id, 2, n, s.clock) !== null) changed = true;
  }
  return changed;
};

/** The variants of a skill the hero has learned, in tree order. */
export const knownVariants = (h: HeroState, skill: SkillId): ForkId[] => {
  const out: ForkId[] = [];
  for (const n of SKILL_TREES[h.heroClass]) if (n.effect.kind === 'variant' && n.effect.skill === skill && (h.skills[n.id] ?? 0) > 0) out.push(n.effect.fork);
  return out;
};

/** A preparation built from what the hero knows: each slot's first skill, with its first variant learned. */
export const defaultLoadout = (h: HeroState): Loadout => {
  const pick = (slot: 0 | 1 | 2): LoadoutPick => {
    const skill = knownSkills(h, slot)[0] as SkillId;
    return { skill, variant: knownVariants(h, skill)[0] ?? null };
  };
  return { picks: [pick(0), pick(1), pick(2)] };
};

/**
 * The preparation a kind of fight uses. Whatever the hero no longer knows (after starting over) falls
 * back: the slot's first skill, no variant.
 */
export const loadoutFor = (h: HeroState, mode: CarryMode): Loadout => loadoutAt(h, h.carry?.[mode] ?? 0);

/** A preparation by index (the first, or one built from what the hero knows, when there's none there), as it would be carried. */
export const loadoutAt = (h: HeroState, index: number): Loadout => {
  const l = h.loadouts?.[index] ?? h.loadouts?.[0] ?? defaultLoadout(h);
  const fix = (slot: 0 | 1 | 2): LoadoutPick => {
    const known = knownSkills(h, slot);
    const want = l.picks[slot];
    const skill = known.includes(want.skill) ? want.skill : (known[0] as SkillId);
    const variant = want.variant && skill === want.skill && knownVariants(h, skill).includes(want.variant) ? want.variant : null;
    return { skill, variant };
  };
  const aura = l.aura ?? h.aura;
  const picks: Loadout['picks'] = [fix(0), fix(1), fix(2)];
  const glyphs = carvedGlyphs(h, picks, l.glyphs);
  // The forms follow the pieces this preparation goes in with.
  const forms = chosenForms(l.gear ? { ...h, gear: preparedGear(h, l.gear) } : h, l.forms);
  return {
    ...(l.name ? { name: l.name } : {}),
    picks,
    ...(aura ? { aura } : {}),
    ...(glyphs ? { glyphs } : {}),
    ...(forms ? { forms } : {}),
    ...(l.gear ? { gear: { ...l.gear } } : {}),
  };
};

/** The hero as it goes into a fight of that kind: wearing what its preparation goes in with (the hero itself when it keeps none). */
export const preparedHero = (h: HeroState, mode: CarryMode): HeroState => {
  const want = (h.loadouts?.[h.carry?.[mode] ?? 0] ?? h.loadouts?.[0])?.gear;
  return want ? { ...h, gear: preparedGear(h, want) } : h;
};

/** A hero's battle init for a fight of that kind: its pieces' worth, its look and what it carries, all from its preparation. */
export const fightKit = (h: HeroState, mode: CarryMode, camp?: Pick<CampaignState, 'saga'>): { gear: GearMods; look?: string } & ReturnType<typeof carried> => {
  const hh = preparedHero(h, mode);
  return { gear: gearMods(hh), ...lookOf(hh), ...carried(hh, mode, camp) };
};

/** The unique piece of the hero's own class worn in a slot, and its awakening stage (null: none). */
export const ownUnique = (h: HeroState, slot: UniqueSlot): { id: UniqueId; stage: number } | null => {
  const it = h.gear[slot];
  if (!it?.unique || UNIQUES[it.unique].cls !== h.heroClass) return null;
  return { id: it.unique, stage: it.awaken ?? 0 };
};

/** The forms a preparation picks, as they'd be kept: only for a piece of the hero's class worn there, awakened to the II. Null when none is left. */
export const chosenForms = (h: HeroState, want: Loadout['forms']): Loadout['forms'] | null => {
  if (!want) return null;
  const out: NonNullable<Loadout['forms']> = {};
  let any = false;
  for (const slot of ['weapon', 'armor', 'amulet'] as const) {
    const k = want[slot];
    const u = ownUnique(h, slot);
    if ((k !== 'a' && k !== 'b') || !u || u.stage < FORM_STAGE.choose) continue;
    out[slot] = k;
    any = true;
  }
  return any ? out : null;
};

/**
 * Why a piece's form doesn't go into the fight with this preparation (docs/UNICAS.md §3), or null when it goes: the
 * piece's skill isn't carried (`noSkill`; the automaton as a swarm too), or the chosen form does what the glyph or
 * variant on that button does (`clash`; a final form only when it says so, as the rain of flasks with the fan).
 */
export const formBlock = (id: UniqueId, l: Pick<Loadout, 'picks' | 'glyphs'>, key: FormKey | 'final'): 'noSkill' | 'clash' | null => {
  const sk = UNIQUES[id].skill;
  if (!sk) return null;
  const slot = l.picks.findIndex((p) => p.skill === sk);
  if (slot < 0) return 'noSkill';
  const fork = l.picks[slot]?.variant ?? null;
  if (id === 'anvilHarness' && fork === 'swarm') return 'noSkill';
  // A final form clashes too (the rain of flasks and the fan glyph both split the grenade): it doesn't stack.
  if (formClash(id, key, fork, l.glyphs?.[slot] ?? null)) return 'clash';
  return null;
};

/**
 * The form bits each unique worn goes into a fight with (content `FORM_BIT`): the chosen form from the II, the final one
 * from the IV, both only with the piece's skill carried, the chosen one only when nothing on its button does the same.
 * Ser Gaspar's guard comes in person when the group heard him (`camp`). Null when none has one.
 */
export const carriedForms = (h: HeroState, l: Loadout, camp?: Pick<CampaignState, 'saga'>): Partial<Record<UniqueId, number>> | null => {
  const out: Partial<Record<UniqueId, number>> = {};
  let any = false;
  for (const slot of ['weapon', 'armor', 'amulet'] as const) {
    const u = ownUnique(h, slot);
    if (!u || u.stage < FORM_STAGE.choose) continue;
    let bits = 0;
    const k = l.forms?.[slot];
    if ((k === 'a' || k === 'b') && !formBlock(u.id, l, k)) bits |= FORM_BIT[k];
    if (u.stage >= FORM_STAGE.final && !formBlock(u.id, l, 'final')) bits |= FORM_BIT.final;
    if (bits & FORM_BIT.final && u.id === 'gasparHammer' && camp?.saga?.flags.includes(DRAGON.allies.gaspar)) bits |= FORM_BIT.gaspar;
    if (!bits) continue;
    out[u.id] = bits;
    any = true;
  }
  return any ? out : null;
};

/** Every piece's forms, for the screens: its two and its final one by key. */
export const formsOfPiece = (id: UniqueId): (typeof UNIQUE_FORMS)[UniqueId] => UNIQUE_FORMS[id];

/**
 * The glyphs a preparation carves, as they'd be carried: only complete ones, on an open slot, that fit the skill
 * carried there, each once (the first slot keeps it). Null when none is left.
 */
export const carvedGlyphs = (h: HeroState, picks: Loadout['picks'], want: Loadout['glyphs']): Loadout['glyphs'] | null => {
  if (!want) return null;
  const open = glyphSlotsOpen(h.level);
  const seen = new Set<GlyphId>();
  const out = ([0, 1, 2] as const).map((slot) => {
    const g = want[slot] ?? null;
    if (!g || slot >= open || seen.has(g) || !glyphOwned(h, g) || !glyphFits(picks[slot].skill, g)) return null;
    seen.add(g);
    return g;
  }) as NonNullable<Loadout['glyphs']>;
  return out.some((g) => g !== null) ? out : null;
};

/** What a hero brings into a fight of that kind, for its battle init: the variants and a paladin's aura. */
export const carried = (
  h: HeroState,
  mode: CarryMode,
  camp?: Pick<CampaignState, 'saga'>,
): { variants: Variants; skills: [SkillId, SkillId, SkillId]; aura?: PaladinAura; glyphs?: Loadout['glyphs']; forms?: Partial<Record<UniqueId, number>> } => {
  const l = loadoutFor(h, mode);
  const forms = carriedForms(h, l, camp);
  return {
    variants: [l.picks[0].variant, l.picks[1].variant, l.picks[2].variant],
    skills: [l.picks[0].skill, l.picks[1].skill, l.picks[2].skill],
    ...(h.heroClass === 'paladin' && l.aura ? { aura: l.aura } : {}),
    ...(l.glyphs ? { glyphs: l.glyphs } : {}),
    ...(forms ? { forms } : {}),
  };
};

/**
 * The conditions that last days a fight's bosses left on live heroes (felled by the witch or a giant, or
 * caught twice by a marked blow): one at a time, the newest stays, and at most one new one a day per hero.
 * Waves, incursions and crypts all end here.
 */
export const afflictAfter = (s: CampaignState, battle: BattleState, now: Ms): void => {
  const hit = battle.stats.afflicted;
  if (!hit) return;
  for (const pid of Object.keys(hit).sort()) {
    const p = s.players.find((x) => x.id === pid);
    const kind = hit[pid];
    if (!p || p.left || !kind) continue;
    if (p.hero.ailedAt !== undefined && now - p.hero.ailedAt < AILMENT_MARKS.everyHours * 3_600_000) continue;
    p.hero.ailment = { kind, until: now + AILMENTS[kind].hours * 3_600_000 };
    p.hero.ailedAt = now;
    event(s, now, 'ailment', { pid, data: { kind } });
  }
};
